import React, { useState, useEffect } from 'react';

interface InsightCardProps {
  image: string;
  webpImage?: string;
  placeholder?: string;
  category: string;
  title: string;
  description: string;
  onReadMore?: () => void;
}

const InsightCard: React.FC<InsightCardProps> = ({
  image,
  webpImage,
  placeholder,
  category,
  title,
  onReadMore
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImage, setCurrentImage] = useState(placeholder || image);

  useEffect(() => {
    // Check WebP support
    const webpSupported = document.createElement('canvas')
      .toDataURL('image/webp')
      .indexOf('data:image/webp') === 0;

    const img = new Image();
    const imageSrc = webpSupported && webpImage ? webpImage : image;

    img.onload = () => {
      setCurrentImage(imageSrc);
      setImageLoaded(true);
    };
    img.src = imageSrc;
  }, [image, webpImage, placeholder]);

  return (
    <div
      className="relative overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer w-[300px] h-[400px] sm:w-[457px] sm:h-[503px]"
      style={{
        borderRadius: '20px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, #022334 84%)'
      }}
      onClick={onReadMore}
    >
      {/* Image */}
      <div
        className="absolute inset-0 transition-all duration-300"
        style={{
          backgroundImage: `url(${currentImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          filter: imageLoaded ? 'none' : 'blur(20px)',
          transform: imageLoaded ? 'scale(1)' : 'scale(1.1)'
        }}
      />

      {/* Multiply blend overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #000000 100%)',
          mixBlendMode: 'multiply'
        }}
      />

      {/* Content */}
      <div className="absolute left-6 bottom-10 sm:left-8 sm:bottom-14 right-6 sm:right-8">
        <h3
          className="text-[20px] sm:text-[23px] lg:text-[28px] mb-3 sm:mb-4"
          style={{
            fontFamily: 'Manrope',
            fontWeight: 700,
            lineHeight: '100%',
            letterSpacing: '-0.011em',
            textAlign: 'left',
            color: '#FFFFFF'
          }}
        >
          {category}
        </h3>
        <p
          className="text-[16px] sm:text-[22px] mb-4 sm:mb-6"
          style={{
            fontFamily: 'Manrope',
            fontWeight: 200,
            lineHeight: '110%',
            letterSpacing: '-0.011em',
            textAlign: 'left',
            color: '#FFFFFF'
          }}
        >
          {title}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReadMore?.();
          }}
          className="flex items-center justify-center"
          style={{
            width: '120px',
            height: '28px',
            padding: '8px 20px',
            background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
            boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
            filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
            backdropFilter: 'blur(50px)',
            borderRadius: '10px',
            border: 'none',
            fontFamily: 'Manrope',
            fontWeight: 400,
            fontSize: '11px',
            lineHeight: '20px',
            letterSpacing: '-0.0001em',
            textAlign: 'center',
            color: '#FFFFFF',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
        >
          Read More
        </button>
      </div>
    </div>
  );
};

export default InsightCard;
